/**
 * Generate Jig model-building module.
 * Downloads model assets, loads OBJ geometry, maps MEME metadata into Jig model
 * structures, and optionally builds scene context for viewpoint planning.
 */
import { LibraryEntry } from '@/store/modules/teamlibrary/types'
import axios from 'axios'
import * as THREE from 'three'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { buildModelSceneSource, buildPreparedSceneContextFromSource } from './generate-jig.model-scene'
import type {
  BuiltModelData,
  DownloadedModelFiles,
  GenerateJigContext,
  MemeGeometryRecoveryPayload,
  MemeMetaFile,
  MemeMetaLookup,
  MemeMetaMaterial,
  MemeMetaMesh,
  MemeMetaTextureReference,
  MemeMetaTextureResource,
} from './generate-jig.types'
import {
  DEFAULT_MATERIAL_ID,
  generateGuid,
  isRequestCanceled,
  MATERIAL_PRESET_TO_ID,
  normalizeMeshName,
  TEXTURE_TYPE_MAPPING,
} from './generate-jig.utils'


async function downloadArrayBuffer(ctx: GenerateJigContext, url: string): Promise<ArrayBuffer> {
  const response = await axios.get(url, ctx.getRequestConfig({
    headers: ctx.buildJigApiHeaders(true),
    responseType: 'arraybuffer',
  }))

  return response.data as ArrayBuffer
}

async function downloadBlob(ctx: GenerateJigContext, url: string): Promise<Blob> {
  const buffer = await downloadArrayBuffer(ctx, url)
  return new Blob([buffer], { type: 'application/octet-stream' })
}

async function verifyMemeExists(ctx: GenerateJigContext, url: string): Promise<ArrayBuffer | null> {
  try {
    await axios.head(url, ctx.getRequestConfig({
      headers: ctx.buildJigApiHeaders(true),
    }))
    return null
  } catch (_headError) {
    if (isRequestCanceled(_headError)) {
      throw _headError
    }
  }

  try {
    const response = await axios.get(url, ctx.getRequestConfig({
      headers: ctx.buildJigApiHeaders(true),
      responseType: 'arraybuffer',
    }))
    return response && response.data ? (response.data as ArrayBuffer) : null
  } catch (_fullProbeError) {
    if (isRequestCanceled(_fullProbeError)) {
      throw _fullProbeError
    }
    throw new Error('MEME file is missing or inaccessible for this model')
  }
}

async function downloadMemeMeta(ctx: GenerateJigContext, url: string): Promise<MemeMetaFile | null> {
  try {
    const response = await axios.get(url, ctx.getRequestConfig({
      headers: ctx.buildJigApiHeaders(true),
    }))

    const data = response && response.data ? response.data : null

    if (data && data.Asset3DData && Array.isArray(data.Asset3DData.Meshes)) {
      return data as MemeMetaFile
    }

    return null
  } catch (_error) {
    if (isRequestCanceled(_error)) {
      throw _error
    }
    return null
  }
}

/**
 * Downloads the OBJ and optional MEMEMETA companion files for a library model
 * after first verifying the source MEME payload is reachable.
 */
export async function downloadModelFiles(ctx: GenerateJigContext, libraryEntry: LibraryEntry): Promise<DownloadedModelFiles> {
  const item = libraryEntry.Item
  const itemUrl = item && item.ItemUrl ? item.ItemUrl : ''
  const mememetaUrl = item && item.MemeMetaURL
    ? item.MemeMetaURL
    : `/tenants/v1/${ctx.tenantId}/library/v1/${libraryEntry.Id}/mememeta/v1`

  if (!itemUrl) {
    throw new Error('Library ItemUrl is missing')
  }

  const memePath = itemUrl
  const objPath = itemUrl.replace(/\.meme$/i, '.obj')

  const objUrl = ctx.apiDomain + objPath
  const memeUrl = ctx.apiDomain + memePath
  const memeMetaUrl = ctx.apiDomain + mememetaUrl

  const probedMemeBuffer = await verifyMemeExists(ctx, memeUrl)

  const [objBlob, memeBuffer, memeMeta] = await Promise.all([
    downloadBlob(ctx, objUrl).catch((e) => {
      throw new Error('Failed to download OBJ file: ' + (e instanceof Error ? e.message : String(e)))
    }),
    probedMemeBuffer
      ? Promise.resolve<ArrayBuffer | null>(probedMemeBuffer)
      : downloadArrayBuffer(ctx, memeUrl).catch((_e) => {
        if (isRequestCanceled(_e)) {
          throw _e
        }
        return null
      }),
    downloadMemeMeta(ctx, memeMetaUrl),
  ])

  return {
    objBlob: objBlob,
    memeUri: memePath,
    memeBuffer: memeBuffer,
    memeMeta: memeMeta,
  }
}

/**
 * Loads the downloaded OBJ blob into a Three.js object graph while ensuring the
 * temporary object URL is always revoked.
 */
export async function loadObjFromBlob(blob: Blob): Promise<THREE.Object3D> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob)
    const loader = new OBJLoader()

    loader.load(
      objectUrl,
      (object: THREE.Group) => {
        URL.revokeObjectURL(objectUrl)
        resolve(object)
      },
      undefined,
      (error: unknown) => {
        URL.revokeObjectURL(objectUrl)
        reject(error)
      }
    )
  })
}

function createMemeMetaLookup(memeMetaData: MemeMetaFile | null): MemeMetaLookup | null {
  if (!memeMetaData || !memeMetaData.Asset3DData) {
    return null
  }

  const meshes = Array.isArray(memeMetaData.Asset3DData.Meshes) ? memeMetaData.Asset3DData.Meshes : []
  const materials = Array.isArray(memeMetaData.Asset3DData.Materials) ? memeMetaData.Asset3DData.Materials : []
  const textureResources = Array.isArray(memeMetaData.Asset3DData.TextureResources) ? memeMetaData.Asset3DData.TextureResources : []

  const meshNameToData = new Map<string, MemeMetaMesh>()
  const meshToMaterial = new Map<string, MemeMetaMaterial>()
  const textureIdToData = new Map<string, MemeMetaTextureResource>()

  meshes.forEach((mesh: MemeMetaMesh) => {
    if (!mesh || !mesh.Name) {
      return
    }
    meshNameToData.set(normalizeMeshName(mesh.Name), mesh)
  })

  meshes.forEach((mesh: MemeMetaMesh) => {
    if (!mesh || !mesh.MaterialID || !mesh.Name) {
      return
    }

    const material = materials.find((item: MemeMetaMaterial) => item && item.MaterialID === mesh.MaterialID)

    if (material) {
      meshToMaterial.set(normalizeMeshName(mesh.Name), material)
    }
  })

  textureResources.forEach((texture: MemeMetaTextureResource) => {
    if (!texture || !texture.TextureID) {
      return
    }
    textureIdToData.set(texture.TextureID, texture)
  })

  return {
    meshNameToData,
    meshToMaterial,
    textureIdToData,
  }
}

function determineMaterialPreset(materialName: string): string {
  const uppercaseName = materialName ? materialName.toUpperCase() : ''

  if (uppercaseName.indexOf('JIG') !== 0) {
    return 'UserDefined'
  }

  let baseName = materialName.substring(3)
  baseName = baseName.replace(/^[_\s]+/, '')
  baseName = baseName.replace(/_Inst\d+$/i, '')
  baseName = baseName.replace(/_/g, ' ').trim().toUpperCase()

  if (baseName === 'X-RAY' || baseName === 'XRAY') {
    return 'XRay'
  }
  if (baseName === 'UNLIT') {
    return 'Unlit'
  }
  if (baseName === 'FLOW') {
    return 'Flow'
  }
  if (baseName === 'STANDARD') {
    return 'Standard'
  }

  return 'PresetBasic'
}

function getMaterialIdForPreset(preset: string): number {
  return MATERIAL_PRESET_TO_ID[preset] || DEFAULT_MATERIAL_ID
}

function convertTextureType(memeMetaType: string): string | null {
  if (!memeMetaType) {
    return null
  }

  return TEXTURE_TYPE_MAPPING[memeMetaType] || null
}

function hasRmaTexture(material: MemeMetaMaterial): boolean {
  if (!material || !Array.isArray(material.TextureReferenceData)) {
    return false
  }

  return material.TextureReferenceData.some((textureRef: MemeMetaTextureReference) => textureRef && textureRef.Property === 'RMA')
}

function remap(value: number, fromMin: number, fromMax: number, toMin: number, toMax: number): number {
  return ((value - fromMin) / (fromMax - fromMin)) * (toMax - toMin) + toMin
}

function convertMaterialValues(material: MemeMetaMaterial): any {
  const colorInput = material && material.Color ? material.Color : { r: 1, g: 1, b: 1 }
  const emissionInput = material && material.Emission ? material.Emission : { r: 0, g: 0, b: 0 }
  const alpha = material && typeof material.Alpha === 'number' ? material.Alpha : 1
  const normalIntensity = material && typeof material.NormalIntensity === 'number' ? material.NormalIntensity : 1
  const roughnessInput = material && typeof material.Roughness === 'number' ? material.Roughness : 0.5
  const metallicInput = material && typeof material.Metallic === 'number' ? material.Metallic : 0

  const color = {
    r: colorInput.r,
    g: colorInput.g,
    b: colorInput.b,
    a: alpha,
  }

  const emission = {
    r: emissionInput.r,
    g: emissionInput.g,
    b: emissionInput.b,
  }

  if (hasRmaTexture(material)) {
    return {
      color: color,
      emission: emission,
      metallic: 1,
      glossiness: 1,
      normalIntensity: normalIntensity,
      occlusion: 1,
    }
  }

  const metallic = remap(metallicInput, 0, 1, 0, 2)
  const roughness = remap(roughnessInput, 0, 1, 0, 2)

  return {
    color: color,
    emission: emission,
    metallic: metallic,
    glossiness: 2 - roughness,
    normalIntensity: normalIntensity,
    occlusion: 0,
  }
}

function buildTextureSerializedValue(textureUid: string): string {
  const dataString = '{\\\"UID\\\":\\\"' + textureUid + '\\\"}'
  return '{"Type":0,"_dataString":"' + dataString + '"}'
}

function createMaterialProperties(diffuseTexture: string, normalTexture: string, rmaTexture: string, materialValues: any | null): any {
  return {
    _items: [
      {
        Name: '_Color',
        Type: 0,
        SerializedValue: materialValues
          ? '{"r":' + materialValues.color.r + ',"g":' + materialValues.color.g + ',"b":' + materialValues.color.b + ',"a":' + materialValues.color.a + '}'
          : '{"r":1.0,"g":1.0,"b":1.0,"a":1.0}',
      },
      {
        Name: '_MainTex',
        Type: 4,
        SerializedValue: diffuseTexture,
      },
      {
        Name: '_EmissionColor',
        Type: 0,
        SerializedValue: materialValues
          ? '{"r":' + materialValues.emission.r + ',"g":' + materialValues.emission.g + ',"b":' + materialValues.emission.b + ',"a":1.0}'
          : '{"r":0.0,"g":0.0,"b":0.0,"a":1.0}',
      },
      {
        Name: '_BumpMap',
        Type: 4,
        SerializedValue: normalTexture,
      },
      {
        Name: '_BumpScale',
        Type: 2,
        SerializedValue: materialValues ? String(materialValues.normalIntensity) : '1',
      },
      {
        Name: '_MetallicGlossMap',
        Type: 4,
        SerializedValue: rmaTexture,
      },
      {
        Name: '_Glossiness',
        Type: 3,
        SerializedValue: materialValues ? String(materialValues.glossiness) : '1.5',
      },
      {
        Name: '_Metallic',
        Type: 3,
        SerializedValue: materialValues ? String(materialValues.metallic) : '0',
      },
      {
        Name: '_OcclusionStrength',
        Type: 3,
        SerializedValue: materialValues ? String(materialValues.occlusion) : '0',
      },
    ],
  }
}

function resolveMeshMaterial(
  normalizedMeshName: string,
  memeMetaLookup: MemeMetaLookup | null,
  memeMetaTextures: Map<string, any>
): { materialId: number; materialValues: any } {
  let materialId = DEFAULT_MATERIAL_ID
  let materialValues: any = null

  if (!memeMetaLookup) {
    return { materialId, materialValues }
  }

  const meshMaterial = memeMetaLookup.meshToMaterial.get(normalizedMeshName)
  if (!meshMaterial) {
    return { materialId, materialValues }
  }

  const materialPreset = determineMaterialPreset(meshMaterial.MaterialName || '')
  materialId = getMaterialIdForPreset(materialPreset)
  materialValues = convertMaterialValues(meshMaterial)

  if (Array.isArray(meshMaterial.TextureReferenceData)) {
    meshMaterial.TextureReferenceData.forEach((textureRef: MemeMetaTextureReference) => {
      if (!textureRef || !textureRef.TextureID) {
        return
      }

      const textureResource = memeMetaLookup.textureIdToData.get(textureRef.TextureID)
      if (!textureResource) {
        return
      }

      const jigTextureType = convertTextureType(String(textureRef.Property || ''))
      if (!jigTextureType) {
        return
      }

      const textureUid = generateGuid()
      const textureInfo = {
        uid: textureUid,
        uri: textureResource.URL,
        fileName: textureResource.Name,
        textureType: jigTextureType,
        md5: textureResource.Md5 || '',
      }

      memeMetaTextures.set(normalizedMeshName + ':' + jigTextureType, textureInfo)
    })
  }

  return { materialId, materialValues }
}

function buildMeshSerializedElement(
  mesh: THREE.Mesh,
  meshFullName: string,
  modelUid: string,
  materialId: number,
  materialProperties: any,
  parentName: string
): any {
  return {
    ElementUniqueName: meshFullName,
    ElementIndex: -1, // set by caller
    ModelUID: modelUid,
    ElementData: {
      ObjectName: meshFullName,
      Position: {
        x: parseFloat(mesh.position.x.toFixed(6)),
        y: parseFloat(mesh.position.y.toFixed(6)),
        z: parseFloat(mesh.position.z.toFixed(6)),
      },
      Rotation: {
        x: parseFloat(mesh.quaternion.x.toFixed(6)),
        y: parseFloat(mesh.quaternion.y.toFixed(6)),
        z: parseFloat(mesh.quaternion.z.toFixed(6)),
        w: parseFloat(mesh.quaternion.w.toFixed(6)),
      },
      Scale: {
        x: parseFloat(mesh.scale.x.toFixed(6)),
        y: parseFloat(mesh.scale.y.toFixed(6)),
        z: parseFloat(mesh.scale.z.toFixed(6)),
      },
      Actions: [],
      AutoSpinAxis: { x: 0, y: 0, z: 0 },
      AutoSpinSpeed: 0,
      AutoSpinOnThisStage: false,
      Material: {
        ID: materialId,
        Properties: materialProperties,
      },
      Color: { r: 0, g: 0, b: 0, a: 0 },
      EmissiveColor: { r: 0, g: 0, b: 0, a: 0 },
      Normal: 0,
      Metallic: 0,
      Smoothness: 0,
      Height: 0,
      Occlusion: 0,
      UVScrollXSpeed: 0,
      UVScrollYSpeed: 0,
      Video: {
        VideoUID: '',
        StartSecs: 0,
        EndSecs: 0,
        PreLoad: false,
        Loop: false,
      },
      Label: '',
      LabelOnForThisStage: false,
      ShowcaseOnThisStage: false,
      Deleted: false,
      ParentName: parentName,
      ParentModelUID: modelUid,
      Sound: {
        SoundUUID: '',
        Delay: 0,
        Loop: false,
        Uri: '',
      },
      TextBoxData: {
        FontSize: 0,
        FontColor: { r: 0, g: 0, b: 0, a: 0 },
        Text: '',
      },
    },
  }
}

function buildInnerParentElement(
  modelObject: THREE.Object3D,
  innerParentUid: string,
  innerParentName: string,
  modelUid: string,
  elementIndex: number
): { mesh: any; subMesh: any; serializedElement: any } {
  const mesh = {
    UID: innerParentUid,
    Name: innerParentName,
    Type: 2,
    MaterialId: DEFAULT_MATERIAL_ID,
    Pivot: {
      Position: { x: 0, y: 0, z: 0 },
      Rotation: { x: 0, y: 0, z: 0, w: 1 },
    },
    Origin: {
      Position: { x: 0, y: 0, z: 0 },
      Rotation: { x: 0, y: 0, z: 0, w: 1 },
    },
  }

  const subMesh = {
    UID: innerParentUid,
    Name: 'InnerParent',
  }

  const serializedElement = {
    ElementUniqueName: innerParentName,
    ElementIndex: elementIndex,
    ModelUID: modelUid,
    ElementData: {
      ObjectName: innerParentName,
      Position: {
        x: parseFloat((-modelObject.position.x).toFixed(6)),
        y: parseFloat(modelObject.position.y.toFixed(6)),
        z: parseFloat(modelObject.position.z.toFixed(6)),
      },
      Rotation: {
        x: parseFloat(modelObject.quaternion.x.toFixed(6)),
        y: parseFloat(modelObject.quaternion.y.toFixed(6)),
        z: parseFloat(modelObject.quaternion.z.toFixed(6)),
        w: parseFloat(modelObject.quaternion.w.toFixed(6)),
      },
      Scale: {
        x: parseFloat(modelObject.scale.x.toFixed(6)),
        y: parseFloat(modelObject.scale.y.toFixed(6)),
        z: parseFloat(modelObject.scale.z.toFixed(6)),
      },
      Actions: [],
      AutoSpinAxis: { x: 0, y: 0, z: 0 },
      AutoSpinSpeed: 0,
      AutoSpinOnThisStage: false,
      Material: {
        ID: 0,
        Properties: { _items: [] },
      },
      Color: { r: 0, g: 0, b: 0, a: 0 },
      EmissiveColor: { r: 0, g: 0, b: 0, a: 0 },
      Normal: 0,
      Metallic: 0,
      Smoothness: 0,
      Height: 0,
      Occlusion: 0,
      UVScrollXSpeed: 0,
      UVScrollYSpeed: 0,
      Video: {
        VideoUID: '',
        StartSecs: 0,
        EndSecs: 0,
        PreLoad: false,
        Loop: false,
      },
      Label: '',
      LabelOnForThisStage: false,
      ShowcaseOnThisStage: false,
      Deleted: false,
      ParentName: 'ObjectParent',
      ParentModelUID: '',
      Sound: {
        SoundUUID: '',
        Delay: 0,
        Loop: false,
        Uri: '',
      },
      TextBoxData: {
        FontSize: 0,
        FontColor: { r: 0, g: 0, b: 0, a: 0 },
        Text: '',
      },
    },
  }

  return { mesh, subMesh, serializedElement }
}

function finalizeTextures(memeMetaTextures: Map<string, any>, apiDomain: string): any[] {
  const dedupedTextures: any[] = []
  const seenTextureUids = new Set<string>()

  Array.from(memeMetaTextures.values()).forEach((texture: any) => {
    if (!texture || !texture.uid || seenTextureUids.has(texture.uid)) {
      return
    }

    seenTextureUids.add(texture.uid)
    dedupedTextures.push(texture)
  })

  return dedupedTextures.map((textureInfo: any) => {
    const textureUid = textureInfo.uid || generateGuid()
    const textureUri = textureInfo.uri || ''
    let normalizedUri = textureUri

    if (apiDomain && normalizedUri.indexOf(apiDomain) === 0) {
      normalizedUri = normalizedUri.substring(apiDomain.length)
    }

    return {
      UID: textureUid,
      Uri: normalizedUri,
      FileName: textureInfo.fileName || '',
      TextureType: textureInfo.textureType || '',
      TextureString: '',
      md5: textureInfo.md5 || '',
    }
  })
}

/**
 * Translates the loaded Three.js model plus optional MEME metadata into the Jig
 * model payload shape used by update requests, including meshes, textures, and
 * serialized stage elements.
 */
export function buildModelData(
  apiDomain: string,
  modelObject: THREE.Object3D,
  modelUri: string,
  memeMetaData: MemeMetaFile | null,
  modelFileName: string,
  options: {
    includeSceneContext?: boolean
    recoveredBounds?: MemeGeometryRecoveryPayload | null
  } = {}
): BuiltModelData {
  modelObject.updateMatrixWorld(true)

  const modelUid = generateGuid()
  const innerParentUid = generateGuid()
  const innerParentName = innerParentUid + '_InnerParent'
  const modelCenter = modelObject.userData && modelObject.userData.originalPivot ? modelObject.userData.originalPivot : new THREE.Vector3(0, 0, 0)
  const modelRotation = modelObject.userData && modelObject.userData.originalRotation
    ? modelObject.userData.originalRotation
    : { x: 0, y: 0, z: 0, w: 1 }

  const memeMetaLookup = createMemeMetaLookup(memeMetaData)
  const memeMetaTextures = new Map<string, any>()

  const sceneSource = buildModelSceneSource(modelObject, innerParentName)
  const { meshNodes, objectToFullName } = sceneSource
  const recoveredBounds = options.recoveredBounds || null
  const sceneContext = options.includeSceneContext
    ? buildPreparedSceneContextFromSource(sceneSource, { memeMetaData: memeMetaData, recoveredBounds: recoveredBounds })
    : null

  const meshes: any[] = []
  const subMeshes: any[] = []
  const serializedElements: any[] = []

  const originData = {
    Position: {
      x: parseFloat((-Number((modelCenter as any).x || 0)).toFixed(6)),
      y: parseFloat(Number((modelCenter as any).y || 0).toFixed(6)),
      z: parseFloat(Number((modelCenter as any).z || 0).toFixed(6)),
    },
    Rotation: {
      x: parseFloat(Number((modelRotation as any).x || 0).toFixed(6)),
      y: parseFloat(Number((modelRotation as any).y || 0).toFixed(6)),
      z: parseFloat(Number((modelRotation as any).z || 0).toFixed(6)),
      w: parseFloat(Number((modelRotation as any).w || 1).toFixed(6)),
    },
  }

  meshNodes.forEach((mesh: THREE.Mesh) => {
    const meshFullName = String(objectToFullName.get(mesh) || '')
    const meshUid = meshFullName.split('_')[0]
    const meshName = mesh.name && mesh.name.length > 0 ? mesh.name : 'UnnamedMesh'
    const normalizedMeshName = normalizeMeshName(meshName)

    const { materialId, materialValues } = resolveMeshMaterial(normalizedMeshName, memeMetaLookup, memeMetaTextures)

    const diffuseTextureInfo = memeMetaTextures.get(normalizedMeshName + ':diffuse') || null
    const normalTextureInfo = memeMetaTextures.get(normalizedMeshName + ':normal') || null
    const rmaTextureInfo = memeMetaTextures.get(normalizedMeshName + ':rma') || null

    const diffuseSerialized = diffuseTextureInfo && diffuseTextureInfo.uid
      ? buildTextureSerializedValue(diffuseTextureInfo.uid)
      : '{"Type":0,"_dataString":""}'
    const normalSerialized = normalTextureInfo && normalTextureInfo.uid
      ? buildTextureSerializedValue(normalTextureInfo.uid)
      : '{"Type":0,"_dataString":""}'
    const rmaSerialized = rmaTextureInfo && rmaTextureInfo.uid
      ? buildTextureSerializedValue(rmaTextureInfo.uid)
      : '{"Type":0,"_dataString":""}'

    const parentName = mesh.parent ? String(objectToFullName.get(mesh.parent as THREE.Object3D) || innerParentName) : innerParentName

    meshes.push({
      UID: meshUid,
      Name: meshFullName,
      Type: 5,
      MaterialId: materialId,
      Pivot: {
        Position: { x: 0, y: 0, z: 0 },
        Rotation: { x: 0, y: 0, z: 0, w: 1 },
      },
      Origin: originData,
    })

    subMeshes.push({
      UID: meshUid,
      Name: meshName,
    })

    const element = buildMeshSerializedElement(
      mesh, meshFullName, modelUid, materialId,
      createMaterialProperties(diffuseSerialized, normalSerialized, rmaSerialized, materialValues),
      parentName
    )
    element.ElementIndex = serializedElements.length
    serializedElements.push(element)
  })

  const innerParent = buildInnerParentElement(modelObject, innerParentUid, innerParentName, modelUid, serializedElements.length)
  meshes.push(innerParent.mesh)
  subMeshes.push(innerParent.subMesh)
  serializedElements.push(innerParent.serializedElement)

  const textureEntries = finalizeTextures(memeMetaTextures, apiDomain)
  const textureUIDs = textureEntries.map((entry: any) => entry.UID)

  const modelEntry = {
    UID: modelUid,
    OriginalUID: '',
    Uri: modelUri,
    FullFileName: modelFileName,
    ModelName: modelFileName.replace(/\.[^.]+$/, ''),
    InternalTags: [],
    GeometryString: '',
    FileSizeInBytes: 0,
    OriginalFileSizeInBytes: 0,
    IsFlipped: true,
    IsTrueScale: true,
    SubMeshes: subMeshes,
    Textures: textureUIDs,
    Categories: '',
    TriangleCount: sceneSource.triangleCount,
    Md5: '',
    GeometryCanBeIgnored: false,
  }

  return {
    modelEntry: modelEntry,
    meshes: meshes,
    serializedElements: serializedElements,
    textures: textureEntries,
    hasTexture: textureEntries.length > 0,
    sceneContext: sceneContext,
    sceneSource: sceneSource,
    memeMetaData: memeMetaData,
    recoveredBounds: recoveredBounds,
  }
}

/**
 * Lazily hydrates scene context on existing model data when viewpoint planning was
 * not requested during the initial model-build pass.
 *
 * NOTE: Mutates the input modelData in place (sets sceneContext) and returns
 * the same reference. Callers should not assume a new object is returned.
 */
export function buildSceneContextForModelData(modelData: BuiltModelData): BuiltModelData {
  if (!modelData) {
    return modelData
  }

  if (!modelData.sceneContext && modelData.sceneSource) {
    modelData.sceneContext = buildPreparedSceneContextFromSource(modelData.sceneSource, {
      memeMetaData: modelData.memeMetaData || null,
      recoveredBounds: modelData.recoveredBounds || null,
    })
  }

  return modelData
}
